import React, { useState } from 'react';
import { TextField, Button, Box, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { validateInviteCode, getInviteCodeErrorMessage } from '../../api/alpha';
import './InviteCodeInput.css';

/**
 * Reusable invite code input component with real-time validation
 */
const InviteCodeInput = ({ onValidCode, initialCode = '', size = 'medium' }) => {
  const [code, setCode] = useState(initialCode);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  // Format code: ALPHA-XXXX-XXXX
  const formatCode = (input) => {
    const clean = input.toUpperCase().replace(/[^A-Z0-9-]/g, '');

    // If it starts with ALPHA, format accordingly
    if (clean.startsWith('ALPHA')) {
      const rest = clean.substring(5).replace(/-/g, '');
      if (rest.length <= 4) {
        return rest.length > 0 ? `ALPHA-${rest}` : 'ALPHA-';
      } else {
        return `ALPHA-${rest.substring(0, 4)}-${rest.substring(4, 8)}`;
      }
    }

    return clean;
  };

  const handleChange = (e) => {
    const formatted = formatCode(e.target.value);
    setCode(formatted);
    setResult(null); // Clear previous validation
  };

  const handleValidate = async () => {
    if (!code || code.length < 10) {
      setResult({ valid: false, errorCode: 'INVALID' });
      return;
    }

    setValidating(true);
    try {
      const validation = await validateInviteCode(code);
      setResult(validation);

      if (validation.valid && onValidCode) {
        onValidCode(code, validation);
      }
    } catch (error) {
      setResult({ valid: false, errorCode: 'NETWORK_ERROR' });
    } finally {
      setValidating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleValidate();
    }
  };

  return (
    <Box className="invite-code-input">
      <Box className="invite-code-input-row">
        <TextField
          placeholder="ALPHA-XXXX-XXXX"
          value={code}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          inputProps={{ maxLength: 16 }}
          size={size}
          error={result && !result.valid}
          fullWidth
          variant="outlined"
          className="invite-code-field"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleValidate}
          disabled={validating || !code || code.length < 10}
          size={size}
          className="invite-code-button"
        >
          {validating ? <CircularProgress size={20} color="inherit" /> : 'Validate'}
        </Button>
      </Box>

      {result && (
        <Box className={`validation-result ${result.valid ? 'valid' : 'invalid'}`}>
          {result.valid ? (
            <Typography variant="body2" className="result-text success">
              <CheckCircleIcon fontSize="small" /> Valid code for {result.waveName}
            </Typography>
          ) : (
            <Typography variant="body2" className="result-text error">
              <ErrorIcon fontSize="small" /> {getInviteCodeErrorMessage(result.errorCode)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default InviteCodeInput;
